"use client";

import { useState, useEffect } from "react";
import { addDays, startOfDay, isBefore, format } from "date-fns";

// Public booking specific types
interface AvailableDay {
  date: string;
  slots: Array<{
    start: string;
    end: string;
  }>;
}

interface UseBookingAvailabilityParams {
  bookeeEmail?: string;
  groupEmails?: string[];
  selectedWeek: Date;
  /**
   * Optional explicit end of the availability window. When omitted the window
   * is 7 days from the (clamped) start — the legacy week view. The calendar
   * view passes the end of the visible month here.
   */
  rangeEndDate?: Date;
  meetingDuration: number;
  availableDurations?: number[]; // Allowed durations for event type filtering
  eventType?: string; // Event type for minimum date calculation
  enabled?: boolean;
}

interface UseBookingAvailabilityReturn {
  availability: AvailableDay[] | undefined;
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
}

/**
 * Earliest date a public booker can book for the given event type.
 * Mirrors the clamping applied inside the availability fetch so calendar
 * UIs can disable days and pick the correct starting month.
 */
export function getMinBookableDate(eventType?: string): Date {
  if (eventType && eventType.toLowerCase().includes("sm-costsavings")) {
    // sm-costsavings bookings must be at least 1 week out
    return addDays(startOfDay(new Date()), 7);
  }
  return addDays(startOfDay(new Date()), 1);
}

export function useBookingAvailability({
  bookeeEmail,
  groupEmails,
  selectedWeek,
  rangeEndDate,
  meetingDuration,
  availableDurations,
  eventType,
  enabled = true,
}: UseBookingAvailabilityParams): UseBookingAvailabilityReturn {
  const [availability, setAvailability] = useState<AvailableDay[] | undefined>();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchAvailability = async () => {
    // Support both single email and group emails
    const emails = groupEmails && groupEmails.length > 0
      ? groupEmails
      : (bookeeEmail ? [bookeeEmail] : []);

    if (emails.length === 0 || !meetingDuration || !enabled) {
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      let startDate = startOfDay(selectedWeek);

      const minDate = getMinBookableDate(eventType);
      if (isBefore(startDate, minDate)) {
        startDate = minDate;
      }

      const endDate = rangeEndDate ? startOfDay(rangeEndDate) : addDays(startDate, 7);

      // A visible window entirely before the minimum bookable date (e.g. the
      // current month when bookings must be a week out) has no slots by
      // definition — skip the network round trip.
      if (isBefore(endDate, startDate)) {
        setAvailability([]);
        setIsLoading(false);
        return;
      }

      const response = await fetch('/api/unifiedtime/public-availability', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          // Backward compatible: send single email if only one, otherwise send emails array
          ...(emails.length === 1
            ? { email: emails[0] }
            : { emails: emails }
          ),
          startDate: format(startDate, "yyyy-MM-dd"),
          endDate: format(endDate, "yyyy-MM-dd"),
          slotDuration: meetingDuration,
          availableDurations: availableDurations, // Pass allowed durations for filtering
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        const error = new Error(errorData.error || errorData.message || 'Failed to fetch availability');
        (error as any).code = errorData.code;
        (error as any).statusCode = response.status;
        (error as any).details = errorData.details;
        throw error;
      }

      const data = await response.json();
      setAvailability(data);
    } catch (err: any) {
      // Preserve error details for calendar connection errors
      if (err.code === 'CALENDAR_NOT_CONNECTED' || err.statusCode === 422) {
        const calendarError = new Error(err.message || 'Calendar is not connected');
        (calendarError as any).code = 'CALENDAR_NOT_CONNECTED';
        (calendarError as any).statusCode = 422;
        (calendarError as any).details = err.details;
        setError(calendarError);
      } else {
        setError(err);
      }
      setAvailability(undefined);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchAvailability();
  }, [bookeeEmail, groupEmails, selectedWeek, rangeEndDate, meetingDuration, availableDurations, eventType, enabled]);

  return {
    availability,
    isLoading,
    error,
    refetch: fetchAvailability,
  };
}
